import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Categoria, ProcesoBase, TramiteBase, CategoryAction } from '@app/shared';
import { SelectTramiteDuplicateService } from './select-tramite-duplicate.service';
import { find } from 'lodash';

type Context = 'normal' | 'duplication';

interface ChangeData {
  target: 'category' | 'process' | 'tramite';
  data: Categoria | ProcesoBase | TramiteBase;
}

@Component({
  selector: 'iw-select-tramite-duplicate-process',
  templateUrl: './select-tramite-duplicate.component.html',
  styleUrls: ['./select-tramite-duplicate.component.scss'],
  providers: [SelectTramiteDuplicateService],
  encapsulation: ViewEncapsulation.None
})
export class SelectTramiteDuplicateComponent implements OnInit, OnDestroy {
  // Define cuando el componente se utiliza desde la instancia para realizar la validacion de las consultas por usuario
  @Input() instance = false;
  // Define debe consultar todos los tramites de un proceso tanto los estaticos como duplicados sin contar los genericos
  @Input() allProcess = false;
  // Define si mostrar el check de generico
  @Input() hiddenGeneric = true;
  // Realiza el cambio de generico inicial
  @Input() set isGeneric(g: boolean) {
    this.genericD = g;
    if (g) {
      this.changeCheckGeneric();
    }
  }
  // Define debe consultar todos los tramites de un proceso tanto los estaticos como duplicados sin contar los genericos
  @Input() context: Context = 'normal';
  // Define si el modulo desde donde se llama es administracion basica o no
  @Input() isModuleBasic = false;
  // Define cuando esta seleccionado generico
  genericD = false;
  // Todas las categorias
  categories: Categoria[];
  // Todos los procesos de la categoria
  processes: ProcesoBase[];
  // Todos los tramites(default) del proceso
  tramites: TramiteBase[];
  // Todos los tramites genericos
  generics: TramiteBase[];
  // NgModels
  category: Categoria;
  process: ProcesoBase;
  tramite: TramiteBase;
  genericT: TramiteBase;
  // Trigger para emitir los cambios en la data
  dataObserver: Subject<ChangeData>;
  // Propiedades que se permiten setear masivamente
  private fillable = ['categories', 'processes', 'tramites', 'category', 'process', 'tramite', 'dataObserver'];
  // Controla si se muestra las tareas asociadas
  private destroy: Subject<void> = new Subject<void>();

  constructor(private sts: SelectTramiteDuplicateService) { }

  ngOnInit() { }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Verifica si la variable de entrada 'history' contiene datos para setear
   * las variables descritas en la función 'getAll', de no ser asi el componente
   * realiza una consulta inicial de las categorias para iniciar su ciclo de vida.
   *
   * @memberof SelectTramiteDuplicateComponent
   */
  @Input()
  set history(v: any) {
    if (!v.active) {
      this.dataObserver = new Subject<ChangeData>();
      this.sts
        .getCategories(this.instance)
        .subscribe(data => {
          this.categories = data;
          if (this.instance) {
            this.category = this.categories[0];
            this.selectedCategory(true);
          }
        });
    } else {
      this.setAll(v);
    }
  }

  /**
   * Determina la opción seleccionada en los selects para los filtros.
   *
   * @param {*} item1
   * @param {*} item2
   * @returns
   * @memberof SelectTramiteDuplicateComponent
   */
  isSelected(item1: any, item2: any) {
    if (item1 && item2) {
      return item1.id === item2.id;
    }
    return false;
  }

  /**
   * Evento que se ejecuta al seleccionar una categoría.
   * - Consulta los procesos de una categoria.
   *
   * @param {boolean} [autoSelect=false]
   * @memberof SelectTramiteDuplicateComponent
   */
  selectedCategory(autoSelect: boolean = false) {
    if (this.category && this.category.id) {
      this.sts
        .getProcess(this.category.id, this.instance)
        .subscribe(data => {
          this.processes = data;
          if (this.instance && autoSelect) {
            this.process = this.processes[0];
            this.selectedProcess(true);
          }
        });
    }
    this.tramites = [];
    this.processes = [];
    this.process = undefined;
    this.tramite = undefined;
    this.dataObserver.next({
      target: 'category',
      data: this.category
    });
  }

  /**
   * Evento que se ejecuta al seleccionar un proceso.
   * - Consulta los trámites por default asociados a un proceso.
   *
   * @param {boolean} [autoSelect=false]
   * @memberof SelectTramiteDuplicateComponent
   */
  selectedProcess(autoSelect: boolean = false) {
    this.tramites = [];
    this.tramite = undefined;
    if (this.process && this.process.id) {
      this.sts
        .getDefaultTrtamites(this.process.id, this.instance, this.isModuleBasic)
        .subscribe(data => {
          this.tramites = data;
          if (this.instance && autoSelect) {
            this.tramite = this.tramites[0];
            this.selectedTramite();
          }
        });
    }
    this.dataObserver.next({
      target: 'process',
      data: this.process
    });
  }

  /**
   * Evento que se ejecuta al seleccionar un trámite por default.
   *
   * @memberof SelectTramiteDuplicateComponent
   */
  selectedTramite() {
    this.dataObserver.next({
      target: 'tramite',
      data: this.tramite
    });
  }

  /**
   * Proporciona los valores de n variables locales de forma masiva.
   *
   * @returns
   * @memberof SelectTramiteDuplicateComponent
   */
  getAll() {
    const data = {};
    this.fillable.forEach(key => {
      data[key] = this[key];
    });
    return data;
  }

  /**
   * Recarga las opciones de un select en especifivo.
   *
   * @param {CategoryAction} target
   * @param {(number | string)} [filter=null]
   * @returns {Promise<Boolean>}
   * @memberof SelectTramiteDuplicateComponent
   */
  reload(target: CategoryAction, filter: number | string = null): Promise<Boolean> {
    return new Promise((resolve, reject) => {
      switch (target) {
        case 'category':
          this.category = undefined;
          this.sts
            .getCategories(this.instance)
            .subscribe(data => {
              this.categories = data;
              this.setSelect(target, filter);
              resolve(true);
            }, err => reject(err));
          break;
        case 'process':
          this.process = undefined;
          if (this.category && this.category.id) {
            this.sts
              .getProcess(this.category.id, this.instance)
              .subscribe(data => {
                this.processes = data;
                this.setSelect(target, filter);
                resolve(true);
              }, err => reject(err));
          } else {
            this.processes = [];
            resolve(true);
          }
          break;
        case 'tramite':
          this.tramite = undefined;
          if (this.process && this.process.id) {
            this.sts
              .getDefaultTrtamites(this.process.id, this.instance, this.isModuleBasic)
              .subscribe(data => {
                this.tramites = data;
                this.setSelect(target, filter);
                resolve(true);
              }, err => reject(err));
          } else {
            this.tramites = [];
            resolve(true);
          }
          break;
        case 'genericT':
            this.genericT = undefined;
              this.sts.getDefaultGeneric().subscribe(data => {
                this.generics = data;
                this.genericT = this.generics[0];
                this.setSelect(target, filter);
                resolve(true);
              }, err => reject(err));
            break;
      }
    });
  }

  /**
   * Busca una opción en el en target y la selecciona.
   *
   * @param {('category' | 'process' | 'tramite')} target
   * @param {(number | string)} filter
   * @memberof SelectTramiteDuplicateComponent
   */
  setSelect(target: 'category' | 'process' | 'tramite' | 'genericT', filter: number | string) {
    if (filter) {
      const configFilter = typeof filter === 'number' ? { id: filter } : { nombre: filter };
      const items = {
        category: 'categories',
        process: 'processes',
        tramite: 'tramites',
        genericT: 'generics'
      };
      const obj: any = find(this[items[target]], configFilter);
      if (obj) {
        this[target] = obj;
        if (target == 'tramite'){
          this.selectedTramite();
        }
      }
    }
  }

   /**
   * Controla el cambio de selectores principales a selector generico
   *
   * @memberof SelectTramiteDuplicateComponent
   */
   changeCheckGeneric() {
    if (this.genericD) {
      this.history = {};
      this.processes = [];
      this.tramites = [];
      this.category = undefined;
      this.process = undefined;
      this.tramite = undefined;
      if (!this.generics || !this.generics.length) {
        this.sts.getDefaultGeneric().subscribe(tramites => {
          this.generics = tramites;
          this.genericT = this.generics[0];
        });
      } else {
        this.genericT = this.generics[0];
      }
    } else {
      this.genericT = undefined;
    }
  }

  /**
   * Setea n variables locales, necesarias para restablecer
   * las selecciones del los select realizadas por el usuario
   * en el último ciclo de vida del componente.
   *
   * @private
   * @param {*} data
   * @memberof SelectTramiteDuplicateComponent
   */
  private setAll(data: any) {
    this.fillable.forEach(key => {
      if (data.hasOwnProperty(key)) {
        this[key] = data[key];
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/select-tramite-duplicate/select-tramite-duplicate.component.ts